/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Mantine
import {
	Box,
	Flex,
	Group,
	Menu,
	Image,
	Stack,
	Text,
	Select,
	Tooltip,
	SelectProps,
} from '@mantine/core';

// Styles
import classes from './HeaderUserMenu.module.css';

// Utils
import {
	capitalizeString,
	getRoleLabel,
	truncateString,
} from '@utils/functions';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowDownIcon from '@assets/icons/arrow-down-profil.svg';

// constants
import { NOT_APPLICABLE } from '@config/constants/common';
import {
	IconLogout,
	IconSettings,
	IconLifebuoy,
	IconCheck,
} from '@tabler/icons-react';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useNavigate } from 'react-router';
import { useEffect } from 'react';
import { useUpdateProfile } from '@features/account-settings/api/update-profile';
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */
type TUserButtonProps = {
	image: string;
	firstname?: string;
	lastname?: string;
	role?: string;
	email: string;
	onChangeLanguage: (languageCode: string) => void;
	language: string;
	onLogout: () => void;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

export const UserInfo = ({
	email,
	firstName,
	lastName,
	image,
}: {
	email: string;
	firstName: string;
	lastName: string;
	image: string;
}) => {
	const fullName = firstName + ' ' + lastName;
	return (
		<Box className={classes.containerUserInfo}>
			<Group gap={'10px'} w={'100%'}>
				<img
					src={image}
					className={classes.avatarUserInfo}
				/>
				<Stack gap={'0px'} flex={1}>
					{fullName.length > 15 ? (
						<Tooltip
							label={fullName}
							disabled={fullName.length <= 31}
						>
							<Box>
								<Text className={classes.textName}>
									{truncateString(firstName, 15)}
								</Text>
								<Text className={classes.textName}>
									{truncateString(lastName, 15)}
								</Text>
							</Box>
						</Tooltip>
					) : (
						<Text className={classes.textName}>
							{fullName}
						</Text>
					)}

					<Text className={classes.textEmail}>
						{`${email}`}
					</Text>
				</Stack>
			</Group>
		</Box>
	);
};

type SupportedLanguage = 'fr' | 'en' | 'es' | 'de' | any;
type FlagCode = 'fr' | 'us' | 'es' | 'de';

const languageToFlagCode: Record<
	SupportedLanguage,
	FlagCode
> = {
	fr: 'fr',
	en: 'us',
	es: 'es',
	de: 'de',
};

const getFlagUrl = (
	language: SupportedLanguage
): string => {
	const code = languageToFlagCode[language] || 'us';
	return `https://flagcdn.com/w40/${code}.png`;
};

export const HeaderUserMenu = (
	props: TUserButtonProps
) => {
	const { t, i18n } = useTranslation();

	const LANGUAGE_OPTIONS = [
		{ label: t('global.french'), value: 'fr' },
		{ label: t('global.english'), value: 'en' },
	];

	const [lng, setLng] = useLocalStorage('lng', 'fr');

	const updateProfileMutation = useUpdateProfile({
		mutationConfig: {},
	});

	const { getUser } = useStore();

	const user = getUser();

	const icons: Record<string, React.ReactNode> = {
		fr: (
			<Image
				radius={'xl'}
				h={20}
				w={20}
				src={`https://flagcdn.com/w40/fr.png`}
			/>
		),
		en: (
			<Image
				radius={'xl'}
				h={20}
				w={20}
				src={`https://flagcdn.com/w40/us.png`}
			/>
		),
	};

	useEffect(() => {
		if (props.language) {
			setLng(props.language);
		}
	}, [props.language]);

	const renderSelectOption: SelectProps['renderOption'] =
		({ option, checked }) => (
			<Group flex="1" gap="xs">
				{icons[option.value]}
				{option.label}
				{checked && (
					<IconCheck
						style={{
							marginInlineStart: 'auto',
							width: '18px',
						}}
					/>
				)}
			</Group>
		);
	const navigate = useNavigate();
	return (
		<Menu
			closeOnItemClick={true}
			classNames={classes}
			shadow="md"
		>
			<Menu.Target>
				<Box className={classes.container}>
					<Box className={classes.wrapperImage}>
						<img
							src={props.image}
							className={classes.image}
						/>
					</Box>
					<Tooltip
						disabled={
							props.role
								? t(`roles.${getRoleLabel(props.role)}`)
										.length <= 15
								: true
						}
						label={
							props.role
								? t(`roles.${getRoleLabel(props.role)}`)
								: props.role
						}
					>
						<Box className={classes.wrapperInfos}>
							<Box className={classes.wrapperInfosTop}>
								<Text className={classes.nameText}>
									{`${t('global.hey')} ${capitalizeString(props.firstname ? props.firstname : NOT_APPLICABLE)}`}
								</Text>

								<img src={ArrowDownIcon} />
							</Box>
							<Text className={classes.roleText}>
								{capitalizeString(
									truncateString(
										props.role
											? t(
													`roles.${getRoleLabel(props.role)}`
												)
											: 'USER',
										15
									)
								)}
							</Text>
						</Box>
					</Tooltip>
				</Box>
			</Menu.Target>

			<Menu.Dropdown
				className={classes.containerDropdown}
			>
				<UserInfo
					firstName={capitalizeString(props.firstname)}
					lastName={capitalizeString(props.lastname)}
					email={props.email}
					image={props.image}
				/>
				<Menu.Divider />
				<Menu.Item closeMenuOnClick={false}>
					<Flex
						className={classes.languageSwitch}
						align={'center'}
						gap={'12px'}
						pl={'2px'}
					>
						<Image
							radius={'xl'}
							h={20}
							w={20}
							src={getFlagUrl(lng)}
						/>
						<Select
							classNames={classes}
							variant="unstyled"
							data={LANGUAGE_OPTIONS}
							comboboxProps={{ withinPortal: false }}
							onChange={(_language) => {
								if (_language == null) {
									return;
								}
								i18n.changeLanguage(_language);
								setLng(_language);
								updateProfileMutation.mutate({
									id: user.id,
									locale: {
										language: _language,
									},
								});
							}}
							value={lng ? lng : 'en'}
							defaultValue={props.language}
							rightSection={<img src={ArrowDownIcon} />}
							renderOption={renderSelectOption}
							className={classes.selectLanguage}
						/>
					</Flex>
				</Menu.Item>
				<Menu.Item
					onClick={() => navigate('/account-settings')}
					leftSection={
						<IconSettings color="#292d30" size={24} />
					}
				>
					{t('sidebar.settings')}
				</Menu.Item>
				<Menu.Item
					disabled
					leftSection={
						<IconLifebuoy color="#292d30" size={24} />
					}
				>
					{t('sidebar.support')}
				</Menu.Item>
				<Menu.Divider />

				<Stack pt={'xs'} pb={'xs'}>
					<Menu.Item
						onClick={() => {
							props.onLogout();
						}}
						leftSection={
							<IconLogout color="#292d30" size={24} />
						}
					>
						{t('sidebar.logout')}
					</Menu.Item>
				</Stack>
			</Menu.Dropdown>
		</Menu>
	);
};
